/**
 * PortalSessionReplay — Session Replay viewer
 *
 * Shows the complete path a user took through the scoring engine.
 * View 1: Session list grouped from session_events.
 * View 2: Detailed timeline with score breakdown bar chart.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { supabaseGet } from '@/utils/supabaseRest';
import {
  History, Eye, X, CheckCircle, Flag,
  ArrowLeft, Loader2, Clock, DollarSign,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ResponsiveContainer,
} from 'recharts';

// ─── Types ───────────────────────────────────────────

interface RawEvent {
  session_id: string;
  event_type: 'presented' | 'rejected' | 'converted' | 'session_end';
  vertical_id: string | null;
  phase: number | null;
  score: number | null;
  score_breakdown: Record<string, number> | null;
  revenue: number | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

interface VerticalRef {
  id: string;
  name: string;
}

interface SessionSummary {
  sessionId: string;
  started: string;
  durationMs: number;
  verticalsShown: number;
  converted: number;
  sessionValue: number;
  status: 'active' | 'completed';
  events: RawEvent[];
}

// ─── Constants ───────────────────────────────────────

const EVENT_ICONS: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  presented: { icon: Eye, color: 'text-blue-400', label: 'Presented' },
  rejected: { icon: X, color: 'text-red-400', label: 'Rejected' },
  converted: { icon: CheckCircle, color: 'text-emerald-400', label: 'Converted' },
  session_end: { icon: Flag, color: 'text-amber-400', label: 'Session End' },
};

const SCORE_KEYS = [
  { key: 'intent_score', label: 'Intent Score', color: '#3b82f6' },
  { key: 'qualification_score', label: 'Qualification Score', color: '#8b5cf6' },
  { key: 'revenue_score', label: 'Revenue Score', color: '#10b981' },
  { key: 'conversion_score', label: 'Conversion Score', color: '#06b6d4' },
  { key: 'cross_sell_score', label: 'Cross-Sell Score', color: '#f59e0b' },
  { key: 'call_boost', label: 'Call Boost', color: '#84cc16' },
  { key: 'compliance_penalty', label: 'Compliance Penalty', color: '#ef4444' },
  { key: 'fatigue_penalty', label: 'Fatigue Penalty', color: '#f87171' },
  { key: 'total', label: 'Total', color: '#e2e8f0' },
];

// ─── Helpers ─────────────────────────────────────────

function formatDuration(ms: number): string {
  if (ms < 1000) return '<1s';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (minutes < 60) return `${minutes}m ${secs}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function truncateId(id: string): string {
  return id.length > 12 ? `${id.substring(0, 8)}...` : id;
}

// ─── Component ───────────────────────────────────────

export default function PortalSessionReplay() {
  const [rawEvents, setRawEvents] = useState<RawEvent[]>([]);
  const [verticals, setVerticals] = useState<VerticalRef[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedEventIdx, setSelectedEventIdx] = useState<number | null>(null);

  // ── Data load ──
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const [eventsData, verticalsData] = await Promise.all([
          supabaseGet<RawEvent[]>(
            'session_events?select=session_id,event_type,vertical_id,phase,score,score_breakdown,revenue,metadata,created_at&order=created_at.desc&limit=500'
          ),
          supabaseGet<VerticalRef[]>('verticals?select=id,name'),
        ]);

        if (cancelled) return;
        setRawEvents(eventsData || []);
        setVerticals(verticalsData || []);
      } catch (err) {
        console.warn('SessionReplay: load failed', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  // ── Vertical name map ──
  const verticalMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const v of verticals) m.set(v.id, v.name);
    return m;
  }, [verticals]);

  // ── Group events into sessions ──
  const sessions = useMemo<SessionSummary[]>(() => {
    const grouped = new Map<string, RawEvent[]>();
    for (const evt of rawEvents) {
      const arr = grouped.get(evt.session_id) || [];
      arr.push(evt);
      grouped.set(evt.session_id, arr);
    }

    const result: SessionSummary[] = [];
    for (const [sessionId, events] of grouped) {
      // Sort chronologically within session
      events.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      const started = events[0].created_at;
      const last = events[events.length - 1].created_at;
      const durationMs = new Date(last).getTime() - new Date(started).getTime();

      const uniqueVerticals = new Set(
        events.filter(e => e.vertical_id && e.event_type === 'presented').map(e => e.vertical_id)
      );
      const converted = events.filter(e => e.event_type === 'converted').length;
      const sessionValue = events.reduce((sum, e) => sum + (e.revenue || 0), 0);
      const hasEnd = events.some(e => e.event_type === 'session_end');

      result.push({
        sessionId,
        started,
        durationMs,
        verticalsShown: uniqueVerticals.size,
        converted,
        sessionValue,
        status: hasEnd ? 'completed' : 'active',
        events,
      });
    }

    // Sort by most recent start
    result.sort((a, b) => new Date(b.started).getTime() - new Date(a.started).getTime());
    return result;
  }, [rawEvents]);

  // ── Selected session ──
  const selectedSession = useMemo(
    () => sessions.find(s => s.sessionId === selectedSessionId) || null,
    [sessions, selectedSessionId]
  );

  // ── Score breakdown data for chart ──
  const scoreBreakdownData = useMemo(() => {
    if (selectedEventIdx == null || !selectedSession) return null;
    const evt = selectedSession.events[selectedEventIdx];
    if (!evt?.score_breakdown) return null;

    const data: { name: string; value: number; color: string; isNegative: boolean }[] = [];
    for (const sk of SCORE_KEYS) {
      const val = evt.score_breakdown[sk.key];
      if (val != null) {
        data.push({
          name: sk.label,
          value: val,
          color: sk.color,
          isNegative: sk.key.includes('penalty'),
        });
      }
    }
    return data.length > 0 ? data : null;
  }, [selectedSession, selectedEventIdx]);

  // ── Render ──
  if (isLoading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="p-6 max-w-[1400px] mx-auto space-y-6">
        {selectedSession ? (
          <SessionDetail
            session={selectedSession}
            verticalMap={verticalMap}
            selectedEventIdx={selectedEventIdx}
            scoreBreakdownData={scoreBreakdownData}
            onSelectEvent={setSelectedEventIdx}
            onBack={() => { setSelectedSessionId(null); setSelectedEventIdx(null); }}
          />
        ) : (
          <SessionList
            sessions={sessions}
            onSelect={setSelectedSessionId}
          />
        )}
      </div>
    </PortalLayout>
  );
}

// ─── View 1: Session List ────────────────────────────

function SessionList({
  sessions,
  onSelect,
}: {
  sessions: SessionSummary[];
  onSelect: (id: string) => void;
}) {
  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <History className="h-6 w-6 text-emerald-400" />
          Session Replay
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Inspect the complete path users take through the scoring engine
        </p>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-950/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Session ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Started</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Duration</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Verticals</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Converted</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Value</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {sessions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                    No sessions found
                  </td>
                </tr>
              ) : (
                sessions.map((s) => (
                  <tr
                    key={s.sessionId}
                    className="hover:bg-slate-800/30 transition-colors cursor-pointer group"
                    onClick={() => onSelect(s.sessionId)}
                  >
                    <td className="px-4 py-3 text-sm">
                      <span className="font-mono text-emerald-400 group-hover:underline">
                        {truncateId(s.sessionId)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-300">{formatTimestamp(s.started)}</td>
                    <td className="px-4 py-3 text-sm text-slate-300">{formatDuration(s.durationMs)}</td>
                    <td className="px-4 py-3 text-sm text-slate-300 tabular-nums">{s.verticalsShown}</td>
                    <td className="px-4 py-3 text-sm tabular-nums">
                      <span className={s.converted > 0 ? 'text-emerald-400' : 'text-slate-500'}>
                        {s.converted}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm tabular-nums">
                      <span className={s.sessionValue > 0 ? 'text-emerald-400' : 'text-slate-500'}>
                        ${s.sessionValue.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        s.status === 'completed'
                          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                          : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                      }`}>
                        {s.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ─── View 2: Session Detail ──────────────────────────

function SessionDetail({
  session,
  verticalMap,
  selectedEventIdx,
  scoreBreakdownData,
  onSelectEvent,
  onBack,
}: {
  session: SessionSummary;
  verticalMap: Map<string, string>;
  selectedEventIdx: number | null;
  scoreBreakdownData: { name: string; value: number; color: string; isNegative: boolean }[] | null;
  onSelectEvent: (idx: number | null) => void;
  onBack: () => void;
}) {
  const { events } = session;

  // Determine entry vertical
  const entryEvent = events.find(e => e.event_type === 'presented' && e.vertical_id);
  const entryVertical = entryEvent?.vertical_id ? verticalMap.get(entryEvent.vertical_id) || entryEvent.vertical_id : '—';

  // Phase progression
  const phases = [...new Set(events.map(e => e.phase).filter((p): p is number => p != null))].sort();

  // Engagement level from metadata
  const engagementEvt = events.find(e => e.metadata && (e.metadata as Record<string, unknown>).engagement_level);
  const engagementLevel = engagementEvt
    ? String((engagementEvt.metadata as Record<string, unknown>).engagement_level)
    : '—';

  return (
    <>
      {/* Back + header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="text-slate-400 hover:text-white hover:bg-slate-800"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <History className="h-6 w-6 text-emerald-400" />
            Session Replay
          </h1>
          <p className="text-sm text-slate-400 mt-1 font-mono">{session.sessionId}</p>
        </div>
      </div>

      {/* Session summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <SummaryCard label="Entry Vertical" value={entryVertical} />
        <SummaryCard label="Total Value" value={`$${session.sessionValue.toFixed(2)}`} accent="emerald" />
        <SummaryCard label="Duration" value={formatDuration(session.durationMs)} />
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Phase Progression</p>
          <div className="flex items-center gap-1 mt-2 flex-wrap">
            {phases.length === 0 ? (
              <span className="text-slate-500 text-sm">—</span>
            ) : (
              phases.map((p, i) => (
                <React.Fragment key={p}>
                  <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold border border-emerald-500/30">
                    {p}
                  </span>
                  {i < phases.length - 1 && (
                    <ChevronRight className="h-3 w-3 text-slate-600 flex-shrink-0" />
                  )}
                </React.Fragment>
              ))
            )}
          </div>
        </div>
        <SummaryCard label="Engagement" value={engagementLevel} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Timeline */}
        <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-800">
            <h2 className="text-lg font-semibold text-white">Event Timeline</h2>
            <p className="text-xs text-slate-500 mt-1">Click an event to see its score breakdown</p>
          </div>
          <div className="p-4 space-y-0">
            {events.map((evt, idx) => {
              const info = EVENT_ICONS[evt.event_type] || EVENT_ICONS.presented;
              const Icon = info.icon;
              const vertName = evt.vertical_id ? (verticalMap.get(evt.vertical_id) || truncateId(evt.vertical_id)) : '—';

              // Time delta from previous event
              let timeDelta = '';
              if (idx > 0) {
                const prev = new Date(events[idx - 1].created_at).getTime();
                const curr = new Date(evt.created_at).getTime();
                timeDelta = `+${formatDuration(curr - prev)}`;
              }

              const isSelected = selectedEventIdx === idx;

              return (
                <div
                  key={idx}
                  className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-emerald-500/10 border border-emerald-500/20'
                      : 'hover:bg-slate-800/50 border border-transparent'
                  }`}
                  onClick={() => onSelectEvent(isSelected ? null : idx)}
                >
                  {/* Timeline line + icon */}
                  <div className="flex flex-col items-center flex-shrink-0 pt-0.5">
                    <div className={`p-1.5 rounded-full bg-slate-800 border border-slate-700 ${info.color}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    {idx < events.length - 1 && (
                      <div className="w-px h-6 bg-slate-800 mt-1" />
                    )}
                  </div>

                  {/* Event details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-medium ${info.color}`}>{info.label}</span>
                      <span className="text-sm text-slate-300">{vertName}</span>
                      {evt.phase != null && (
                        <span className="inline-flex items-center justify-center h-5 px-1.5 rounded bg-slate-800 text-xs text-slate-400 border border-slate-700">
                          P{evt.phase}
                        </span>
                      )}
                      {evt.score != null && (
                        <span className="text-xs text-slate-400 font-mono">
                          score: {evt.score.toFixed(1)}
                        </span>
                      )}
                      {evt.event_type === 'converted' && evt.revenue != null && (
                        <span className="text-xs text-emerald-400 font-semibold flex items-center gap-0.5">
                          <DollarSign className="h-3 w-3" />
                          {evt.revenue.toFixed(2)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-slate-600">{formatTimestamp(evt.created_at)}</span>
                      {timeDelta && (
                        <span className="text-xs text-slate-500 flex items-center gap-0.5">
                          <Clock className="h-3 w-3" />
                          {timeDelta}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Score breakdown panel */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-800">
            <h2 className="text-lg font-semibold text-white">Score Breakdown</h2>
          </div>
          <div className="p-4">
            {scoreBreakdownData ? (
              <div>
                <ResponsiveContainer width="100%" height={scoreBreakdownData.length * 40 + 20}>
                  <BarChart
                    data={scoreBreakdownData}
                    layout="vertical"
                    margin={{ top: 4, right: 16, bottom: 4, left: 4 }}
                  >
                    <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis
                      dataKey="name"
                      type="category"
                      tick={{ fill: '#94a3b8', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={120}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#0f172a',
                        border: '1px solid #334155',
                        borderRadius: '8px',
                        color: '#e2e8f0',
                        fontSize: '12px',
                      }}
                      formatter={(value: number) => [value.toFixed(2), 'Score']}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {scoreBreakdownData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>

                {/* Legend */}
                <div className="mt-4 space-y-1 border-t border-slate-800 pt-3">
                  {scoreBreakdownData.map((entry) => (
                    <div key={entry.name} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block h-2.5 w-2.5 rounded-sm"
                          style={{ backgroundColor: entry.color }}
                        />
                        <span className={entry.isNegative ? 'text-red-400' : entry.name === 'Total' ? 'text-white font-bold' : 'text-slate-400'}>
                          {entry.name}
                        </span>
                      </div>
                      <span className={`font-mono ${entry.isNegative ? 'text-red-400' : entry.name === 'Total' ? 'text-white font-bold' : 'text-slate-300'}`}>
                        {entry.value.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Eye className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Click an event with a score to view its breakdown</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Sub-components ──────────────────────────────────

function SummaryCard({ label, value, accent }: {
  label: string;
  value: string;
  accent?: 'emerald' | 'red';
}) {
  const valueColor = accent === 'emerald'
    ? 'text-emerald-400'
    : accent === 'red'
    ? 'text-red-400'
    : 'text-white';

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
      <p className={`text-xl font-bold mt-1 truncate ${valueColor}`}>{value}</p>
    </div>
  );
}
